/**
 * Guest Data Service
 * Handles data storage for both guest (localStorage) and authenticated (API) users
 * Provides a unified interface for data operations
 */

import { authenticatedFetch, API_BASE_URL } from '../config/api';

const STORAGE_KEYS = {
  CABINET: 'guest_cabinet',
  ROUTINES_AM: 'guest_routines_AM',
  ROUTINES_PM: 'guest_routines_PM',
  CUSTOM_ROUTINES: 'guest_custom_routines',
  LAST_ROUTINE_UPDATE: 'guest_last_routine_update',
  PRODUCTS_IN_ROUTINES: 'guest_products_in_routines'
};

class GuestDataService {
  /**
   * Check if user is authenticated
   */
  isAuthenticated(user) {
    return user && user.id;
  }

  /**
   * Get user's products (cabinet)
   */
  async getProducts(user) {
    if (this.isAuthenticated(user)) {
      // Fetch from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/products/${user.id}`);
        if (response.ok) {
          return await response.json();
        }
        return [];
      } catch (error) {
        console.error('Error fetching products from API:', error);
        return [];
      }
    } else {
      // Get from localStorage
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.CABINET);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Error reading products from localStorage:', error);
        return [];
      }
    }
  }

  /**
   * Add product to cabinet
   */
  async addProduct(user, productId) {
    if (this.isAuthenticated(user)) {
      // Save to API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ productId }),
        });
        return response.ok;
      } catch (error) {
        console.error('Error adding product to API:', error);
        return false;
      }
    } else {
      // Save to localStorage
      try {
        const products = await this.getProducts(user);
        // Check if product already exists
        if (!products.find(p => p.product_id === productId)) {
          // Fetch product details from API (public endpoint)
          const productResponse = await fetch(`${API_BASE_URL}/api/products/${productId}`);
          if (productResponse.ok) {
            const productData = await productResponse.json();
            const newProduct = {
              ...productData,
              added_date: new Date().toISOString()
            };
            products.push(newProduct);
            localStorage.setItem(STORAGE_KEYS.CABINET, JSON.stringify(products));
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Error adding product to localStorage:', error);
        return false;
      }
    }
  }

  /**
   * Remove product from cabinet
   */
  async removeProduct(user, productId) {
    if (this.isAuthenticated(user)) {
      // Delete from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/products/${user.id}/${productId}`, {
          method: 'DELETE',
        });
        return response.ok;
      } catch (error) {
        console.error('Error removing product from API:', error);
        return false;
      }
    } else {
      // Remove from localStorage
      try {
        const products = await this.getProducts(user);
        const filtered = products.filter(p => p.product_id !== productId);
        localStorage.setItem(STORAGE_KEYS.CABINET, JSON.stringify(filtered));
        return true;
      } catch (error) {
        console.error('Error removing product from localStorage:', error);
        return false;
      }
    }
  }

  /**
   * Get routine for a specific type and day
   */
  async getRoutine(user, routineType, dayOfWeek) {
    if (this.isAuthenticated(user)) {
      // Fetch from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${routineType}/${dayOfWeek}`);
        if (response.ok) {
          return await response.json();
        }
        return [];
      } catch (error) {
        console.error('Error fetching routine from API:', error);
        return [];
      }
    } else {
      // Get from localStorage
      try {
        const key = routineType === 'AM' ? STORAGE_KEYS.ROUTINES_AM : STORAGE_KEYS.ROUTINES_PM;
        const stored = localStorage.getItem(key);
        if (stored) {
          const routines = JSON.parse(stored);
          return routines[dayOfWeek] || [];
        }
        return [];
      } catch (error) {
        console.error('Error reading routine from localStorage:', error);
        return [];
      }
    }
  }

  /**
   * Save routine for a specific type and day
   */
  async saveRoutine(user, routineType, dayOfWeek, routineData) {
    if (this.isAuthenticated(user)) {
      // Save to API: server expects { order: [ { product_id } | { action_id }, ... ] }
      try {
        const order = (routineData || []).map((item) => {
          if (item && item.product_id != null) return { product_id: item.product_id };
          if (item && item.actionId != null) return { action_id: item.actionId };
          return null;
        }).filter(Boolean);
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${routineType}/${dayOfWeek}/order`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ order }),
        });
        return response.ok;
      } catch (error) {
        console.error('Error saving routine to API:', error);
        return false;
      }
    } else {
      // Save to localStorage
      try {
        const key = routineType === 'AM' ? STORAGE_KEYS.ROUTINES_AM : STORAGE_KEYS.ROUTINES_PM;
        const stored = localStorage.getItem(key);
        const routines = stored ? JSON.parse(stored) : {};
        routines[dayOfWeek] = routineData;
        localStorage.setItem(key, JSON.stringify(routines));
        return true;
      } catch (error) {
        console.error('Error saving routine to localStorage:', error);
        return false;
      }
    }
  }

  /**
   * Get custom routines
   */
  async getCustomRoutines(user) {
    if (this.isAuthenticated(user)) {
      // Fetch from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${user.id}/custom`);
        if (response.ok) {
          return await response.json();
        }
        return [];
      } catch (error) {
        console.error('Error fetching custom routines from API:', error);
        return [];
      }
    } else {
      // Get from localStorage
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.CUSTOM_ROUTINES);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Error reading custom routines from localStorage:', error);
        return [];
      }
    }
  }

  /**
   * Create custom routine
   */
  async createCustomRoutine(user, routineData) {
    if (this.isAuthenticated(user)) {
      // Save to API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${user.id}/custom`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(routineData),
        });
        if (response.ok) {
          return await response.json();
        }
        return null;
      } catch (error) {
        console.error('Error creating custom routine in API:', error);
        return null;
      }
    } else {
      // Save to localStorage
      try {
        const routines = await this.getCustomRoutines(user);
        const newRoutine = {
          id: Date.now(), // Temporary ID
          routine_type: routineData.routineType,
          routine_name: routineData.routineName,
          description: routineData.description,
          icon: routineData.icon,
          created_at: new Date().toISOString()
        };
        routines.push(newRoutine);
        localStorage.setItem(STORAGE_KEYS.CUSTOM_ROUTINES, JSON.stringify(routines));
        return newRoutine;
      } catch (error) {
        console.error('Error creating custom routine in localStorage:', error);
        return null;
      }
    }
  }

  /**
   * Delete custom routine
   */
  async deleteCustomRoutine(user, routineType) {
    if (this.isAuthenticated(user)) {
      // Delete from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${user.id}/custom/${routineType}`, {
          method: 'DELETE',
        });
        return response.ok;
      } catch (error) {
        console.error('Error deleting custom routine from API:', error);
        return false;
      }
    } else {
      // Remove from localStorage
      try {
        const routines = await this.getCustomRoutines(user);
        const filtered = routines.filter(r => r.routine_type !== routineType);
        localStorage.setItem(STORAGE_KEYS.CUSTOM_ROUTINES, JSON.stringify(filtered));
        return true;
      } catch (error) {
        console.error('Error deleting custom routine from localStorage:', error);
        return false;
      }
    }
  }

  /**
   * Get products in routines
   */
  async getProductsInRoutines(user) {
    if (this.isAuthenticated(user)) {
      // Fetch from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/products/${user.id}/in-routines`);
        if (response.ok) {
          return await response.json();
        }
        return [];
      } catch (error) {
        console.error('Error fetching products in routines from API:', error);
        return [];
      }
    } else {
      // Get from localStorage
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.PRODUCTS_IN_ROUTINES);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Error reading products in routines from localStorage:', error);
        return [];
      }
    }
  }

  /**
   * Get last routine update timestamp
   */
  async getLastRoutineUpdate(user) {
    if (this.isAuthenticated(user)) {
      // Fetch from API
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${user.id}/last-update`);
        if (response.ok) {
          const data = await response.json();
          return data.lastUpdated || null;
        }
        return null;
      } catch (error) {
        console.error('Error fetching last routine update from API:', error);
        return null;
      }
    } else {
      // Get from localStorage
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.LAST_ROUTINE_UPDATE);
        return stored || null;
      } catch (error) {
        console.error('Error reading last routine update from localStorage:', error);
        return null;
      }
    }
  }

  /**
   * Update last routine update timestamp
   */
  async updateLastRoutineUpdate(user) {
    const timestamp = new Date().toISOString();
    if (this.isAuthenticated(user)) {
      // This is typically handled by the backend when routines are updated
      // But we can store it locally for consistency
      return true;
    } else {
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEYS.LAST_ROUTINE_UPDATE, timestamp);
        return true;
      } catch (error) {
        console.error('Error updating last routine update in localStorage:', error);
        return false;
      }
    }
  }

  /**
   * Update routines for guest users - organizes products by category into AM/PM routines
   */
  async updateRoutinesForGuest(user) {
    if (this.isAuthenticated(user)) {
      // For authenticated users, this should call the API
      // But we'll handle that in the component
      return false;
    }

    try {
      const products = await this.getProducts(user);
      if (products.length === 0) {
        console.log('No products in cabinet to organize');
        return false;
      }

      // Category order for AM and PM routines
      const amCategoryOrder = [
        'cleanser', 'toner', 'facial mist', 'serum', 'treatment', 
        'eye care', 'all in one', 'moisturizer', 'sunscreen', 'lip care'
      ];
      
      const pmCategoryOrder = [
        'cleanser', 'toner', 'facial mist', 'face mask', 'serum', 
        'eye care', 'treatment', 'all in one', 'moisturizer', 'lip care'
      ];

      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      
      // Resolve a product's type/category for matching
      const getProductTypeOrCategory = (product) => {
        const candidates = [
          product.type,
          product.category,
          Array.isArray(product.types) && product.types[0]
        ].filter(Boolean);
        for (const c of candidates) {
          const s = String(c).trim();
          if (s) return s;
        }
        return null;
      };

      // Organize products by category (flexible match; ensures all products are placed somewhere)
      const organizeByCategory = (categoryOrder) => {
        const organized = [];
        const usedProductIds = new Set();
        
        // Add products in category order
        for (const category of categoryOrder) {
          const categoryLower = category.toLowerCase().trim();
          const categoryProducts = products.filter((p) => {
            if (usedProductIds.has(p.product_id)) return false;
            const typeOrCategory = getProductTypeOrCategory(p);
            if (!typeOrCategory) return false;
            const value = String(typeOrCategory).toLowerCase().trim();
            return (
              value === categoryLower ||
              value.includes(categoryLower) ||
              categoryLower.includes(value)
            );
          });
          
          categoryProducts.forEach(product => {
            organized.push({
              ...product,
              step_order: organized.length + 1,
              routine_type: categoryOrder === amCategoryOrder ? 'AM' : 'PM',
              day_of_week: 'Monday' // Same routine for all days for guests
            });
            usedProductIds.add(product.product_id);
          });
        }

        // Add any remaining products that didn't match a category to the end
        products.forEach((product) => {
          if (usedProductIds.has(product.product_id)) return;
          organized.push({
            ...product,
            step_order: organized.length + 1,
            routine_type: categoryOrder === amCategoryOrder ? 'AM' : 'PM',
            day_of_week: 'Monday'
          });
          usedProductIds.add(product.product_id);
        });

        return organized;
      };

      const amRoutine = organizeByCategory(amCategoryOrder);
      const pmRoutine = organizeByCategory(pmCategoryOrder);

      // Save routines for all days (same routine for each day for guests)
      const amRoutines = {};
      const pmRoutines = {};
      
      days.forEach(day => {
        amRoutines[day] = amRoutine;
        pmRoutines[day] = pmRoutine;
      });

      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.ROUTINES_AM, JSON.stringify(amRoutines));
      localStorage.setItem(STORAGE_KEYS.ROUTINES_PM, JSON.stringify(pmRoutines));
      
      // Update timestamp
      await this.updateLastRoutineUpdate(user);
      
      // Update products in routines list
      const allRoutineProducts = [...amRoutine, ...pmRoutine];
      localStorage.setItem(STORAGE_KEYS.PRODUCTS_IN_ROUTINES, JSON.stringify(allRoutineProducts));

      console.log('✅ Guest routines updated successfully');
      return true;
    } catch (error) {
      console.error('❌ Error updating guest routines:', error);
      return false;
    }
  }

  /**
   * Migrate all guest data to server when user signs up/logs in
   */
  async migrateGuestDataToServer(user) {
    if (!user || !user.id) {
      console.error('Cannot migrate: user not authenticated');
      return false;
    }

    try {
      // Migrate products (cabinet)
      const guestProducts = await this.getProducts(null);
      if (guestProducts.length > 0) {
        for (const product of guestProducts) {
          await this.addProduct(user, product.product_id);
        }
        console.log(`✅ Migrated ${guestProducts.length} products to server`);
      }

      // Migrate custom routines
      const guestCustomRoutines = await this.getCustomRoutines(null);
      if (guestCustomRoutines.length > 0) {
        for (const routine of guestCustomRoutines) {
          await this.createCustomRoutine(user, {
            routineType: routine.routine_type,
            routineName: routine.routine_name,
            description: routine.description,
            icon: routine.icon
          });
        }
        console.log(`✅ Migrated ${guestCustomRoutines.length} custom routines to server`);
      }

      // Migrate routines (AM and PM)
      const routineTypes = ['AM', 'PM'];
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      
      for (const routineType of routineTypes) {
        const key = routineType === 'AM' ? STORAGE_KEYS.ROUTINES_AM : STORAGE_KEYS.ROUTINES_PM;
        const stored = localStorage.getItem(key);
        if (stored) {
          const routines = JSON.parse(stored);
          for (const day of days) {
            if (routines[day] && routines[day].length > 0) {
              await this.saveRoutine(user, routineType, day, routines[day]);
            }
          }
        }
      }
      console.log('✅ Migrated routines to server');

      // Clear guest data from localStorage
      this.clearGuestData();
      console.log('✅ Cleared guest data from localStorage');

      return true;
    } catch (error) {
      console.error('❌ Error migrating guest data:', error);
      return false;
    }
  }

  /**
   * Clear all guest data from localStorage
   */
  clearGuestData() {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('✅ Cleared all guest data');
    } catch (error) {
      console.error('Error clearing guest data:', error);
    }
  }
}

// Export singleton instance
export const guestDataService = new GuestDataService();

